// src/components/dashboard/DashboardContentTabs.js

import { useState, useMemo } from 'react';
import { ChartBarIcon, ArrowTrendingUpIcon, CalendarIcon } from '@heroicons/react/24/outline';

// Components
import UpcomingReviewsChart from './UpcomingReviewsChart';
import SRSLevelsPieChart from './SRSLevelsPieChart';
import ProgressByLevel from './ProgressByLevel';
import Milestones from './Milestones';
import ReviewHeatmap from './ReviewHeatmap';
import StreakCard from './StreakCard';
import AccuracyChart from './AccuracyChart';
import ExtraStudySection from './ExtraStudySection';

const TabButton = ({ active, onClick, children, icon: Icon, tourClass = '', isFirst = false, isLast = false }) => (
  <button
    className={`
      group relative flex w-1/3 items-center justify-center gap-2 px-2 py-4 sm:px-4
      text-sm font-medium transition-all duration-200 ease-in-out
      ${tourClass}
      ${isFirst ? 'rounded-tl-2xl' : ''}
      ${isLast ? 'rounded-tr-2xl' : ''}
      ${
        active
          ? 'z-10 border-b-2 border-primary bg-background-secondary/70 backdrop-blur-sm text-primary'
          : 'border-b-2 border-border bg-background-secondary/70 backdrop-blur-sm text-content-secondary hover:text-content-primary'
      }
    `}
    onClick={onClick}
  >
    {Icon && <Icon className="w-5 h-5 flex-shrink-0" />}
    <span className="truncate">{children}</span>
  </button>
);

const DashboardContentTabs = ({
  stats,
  currentStreak,
  protectedDaysUsed,
  shields,
  protectedDates,
  vacationDates,
  userTimezone,
  isUserHourlySettingEnabled,
  upcomingReviewsSentences,
  upcomingReviewsWords,
  learnedTodayData,
  reviewedSentencesData,
  reviewedWordsData,
  playedSentencesData,
  playedWordsData,
  listening,
  cramWordsCount,
  cramSentencesCount,
  recentUnlocksWordsCount,
  recentUnlocksSentencesCount,
  learnedWordIds,
  learnedSentenceIds,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const { sentencesStats, wordsStats, totalCounts } = stats;

  // Combine skipped + stage 7 for display purposes
  const combinedSentencesSrsData = useMemo(() => {
    if (!sentencesStats?.bySrsStage) return {};
    const data = { ...sentencesStats.bySrsStage };
    if (data.skipped) {
      data[7] = (data[7] || 0) + data.skipped;
      delete data.skipped;
    }
    return data;
  }, [sentencesStats?.bySrsStage]);

  const combinedWordsSrsData = useMemo(() => {
    if (!wordsStats?.bySrsStage) return {};
    const data = { ...wordsStats.bySrsStage };
    if (data.skipped) {
      data[7] = (data[7] || 0) + data.skipped;
      delete data.skipped;
    }
    return data;
  }, [wordsStats?.bySrsStage]);

  return (
    <div className="relative overflow-hidden rounded-2xl bg-background-secondary shadow-md">
      {/* Decorative background bubbles — show through glass cards above */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-16 -right-24 h-72 w-72 rounded-full bg-primary opacity-10" />
        <div className="absolute top-32 -left-24 h-64 w-64 rounded-full bg-secondary opacity-10" />
        <div className="absolute top-8 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full bg-accent opacity-5" />
      </div>
      <div className="flex tab-navigation">
        <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} icon={ChartBarIcon} tourClass="overview-tab" isFirst>
          Overview
        </TabButton>
        <TabButton active={activeTab === 'progress'} onClick={() => setActiveTab('progress')} icon={ArrowTrendingUpIcon} tourClass="progress-tab">
          Progress
        </TabButton>
        <TabButton active={activeTab === 'history'} onClick={() => setActiveTab('history')} icon={CalendarIcon} tourClass="history-tab" isLast>
          History
        </TabButton>
      </div>

      <div className="min-h-[400px] p-6">
        {activeTab === 'overview' && (
          <div className="animate-fade-in space-y-6">
            {(cramWordsCount > 0 || cramSentencesCount > 0 || recentUnlocksWordsCount > 0 || recentUnlocksSentencesCount > 0) && (
              <ExtraStudySection
                cramWordsCount={cramWordsCount}
                cramSentencesCount={cramSentencesCount}
                recentUnlocksWordsCount={recentUnlocksWordsCount}
                recentUnlocksSentencesCount={recentUnlocksSentencesCount}
                learnedWordIds={learnedWordIds}
                learnedSentenceIds={learnedSentenceIds}
              />
            )}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <UpcomingReviewsChart
                data={upcomingReviewsSentences}
                color="var(--color-sentences)"
                gradientId="sentencesGradient"
                isUserHourlySettingEnabled={isUserHourlySettingEnabled}
                type="sentences"
              />
              <UpcomingReviewsChart
                data={upcomingReviewsWords}
                color="var(--color-words)"
                gradientId="wordsGradient"
                isUserHourlySettingEnabled={isUserHourlySettingEnabled}
                type="words"
              />
            </div>
            <div className="milestones-section">
              <Milestones
                sentencesStats={sentencesStats}
                wordsStats={wordsStats}
                totalCounts={totalCounts}
                reviewedSentencesData={reviewedSentencesData}
                reviewedWordsData={reviewedWordsData}
                listeningSeconds={listening?.totalSeconds || 0}
              />
            </div>
          </div>
        )}

        {activeTab === 'progress' && (
          <div className="animate-fade-in space-y-8">
            <div className="grid grid-cols-2 gap-4">
              <SRSLevelsPieChart data={combinedSentencesSrsData} type="sentences" />
              <SRSLevelsPieChart data={combinedWordsSrsData} type="words" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <ProgressByLevel userStats={sentencesStats.byLevel} totalStats={stats.totalCounts.totalSentences} type="sentences" />
              <ProgressByLevel userStats={wordsStats.byLevel} totalStats={stats.totalCounts.totalWords} type="words" />
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="animate-fade-in space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-stretch">
              <div className="flex h-full flex-col">
                <AccuracyChart
                  sentencesAccuracy={sentencesStats.accuracy}
                  wordsAccuracy={wordsStats.accuracy}
                />
              </div>
              <div className="flex h-full flex-col">
                <StreakCard currentStreak={currentStreak} protectedDaysUsed={protectedDaysUsed} shields={shields} userTimezone={userTimezone} sentencesRatio={listening?.sentences?.avgListens} wordsRatio={listening?.words?.avgListens} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <ReviewHeatmap
                title="Items Learned"
                data={learnedTodayData}
                colorVar="--color-session-learn"
                emptyMessage="Start learning to see your progress"
                protectedDates={protectedDates}
                vacationDates={vacationDates}
              />
              <ReviewHeatmap
                title="Sentence Reviews"
                data={reviewedSentencesData}
                playsData={playedSentencesData}
                colorVar="--color-session-review"
                emptyMessage="Complete sentence reviews to track activity"
                protectedDates={protectedDates}
                vacationDates={vacationDates}
              />
              <ReviewHeatmap
                title="Word Reviews"
                data={reviewedWordsData}
                playsData={playedWordsData}
                colorVar="--color-session-review"
                emptyMessage="Complete word reviews to track activity"
                protectedDates={protectedDates}
                vacationDates={vacationDates}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardContentTabs;
